import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import Animated, {
  runOnJS,
  useAnimatedReaction,
  useAnimatedStyle,
  type SharedValue,
} from 'react-native-reanimated';

type TickRulerProps = {
  lines_amount: number;
  spacing_px: number;
  scroll_offset: SharedValue<number>;
  max_line_height: number;
  min_line_height: number;
  big_line_every: number;
  /** Value at tick index 0 — majors when `(line_value_base + index) % big_line_every === 0`. */
  line_value_base?: number;
  line_width: number;
  line_color: string;
  big_line_color: string;
  ruler_stops: boolean;
  /** Track viewport width — used to window ticks (only paint what’s on screen). */
  viewport_width: number;
  /**
   * Extra decorative ticks past min/max so the strip looks continuous at the ends
   * while snap/value still clamp to the real range.
   */
  overscroll_ticks?: number;
};

/** Extra ticks past each edge so scrolling doesn’t flash empty gaps. */
const EDGE_BUFFER_TICKS = 14;
/** Recompute the window only after scrolling this many ticks (cuts JS churn). */
const WINDOW_BUCKET_TICKS = 5;

function positive_mod(n: number, m: number) {
  return ((n % m) + m) % m;
}

function build_tick_window(
  from_raw: number,
  content_first: number,
  content_last: number,
  spacing_px: number,
  viewport_width: number,
) {
  const from = Math.max(content_first, Math.min(content_last, from_raw));
  const visible = Math.ceil(viewport_width / Math.max(1, spacing_px)) + EDGE_BUFFER_TICKS * 2;
  const to = Math.min(content_last, from + Math.max(28, visible));
  return { from, to };
}

/**
 * Tick strip: tick i center-x = i * spacing + scrollOffset (centers under the indicator).
 * Renders optional ghost ticks past the valid range for a continuous end feel.
 */
export const TickRuler = memo(function TickRuler({
  lines_amount,
  spacing_px,
  scroll_offset,
  max_line_height,
  min_line_height,
  big_line_every,
  line_value_base = 0,
  line_width,
  line_color,
  big_line_color,
  ruler_stops,
  viewport_width,
  overscroll_ticks = 0,
}: TickRulerProps) {
  const tick_count = ruler_stops ? lines_amount : lines_amount + 1;
  const last_valid = Math.max(0, tick_count - 1);
  const pad = Math.max(0, Math.floor(overscroll_ticks));
  const content_first = -pad;
  const content_last = last_valid + pad;
  const major_every = Math.max(1, big_line_every);

  /**
   * Layout width covers index 0 → last ghost. Leading ghosts use negative `left`
   * and still paint because overflow is visible (parent track clips the viewport).
   */
  const strip_width = useMemo(() => {
    if (tick_count <= 0) {
      return spacing_px;
    }
    return Math.max(spacing_px, content_last * spacing_px + line_width * 2);
  }, [content_last, line_width, spacing_px, tick_count]);

  const [window_range, setWindowRange] = useState(() =>
    build_tick_window(content_first, content_first, content_last, spacing_px, viewport_width),
  );

  useEffect(() => {
    setWindowRange(build_tick_window(content_first, content_first, content_last, spacing_px, viewport_width));
  }, [content_first, content_last, spacing_px, viewport_width]);

  const apply_window = useCallback((from: number, to: number) => {
    setWindowRange(prev => (prev.from === from && prev.to === to ? prev : { from, to }));
  }, []);

  useAnimatedReaction(
    () => {
      const s = spacing_px;
      if (s <= 0 || viewport_width <= 0 || tick_count <= 0) {
        return -9999;
      }
      const first_raw = Math.floor(-scroll_offset.value / s) - EDGE_BUFFER_TICKS;
      return Math.floor(first_raw / WINDOW_BUCKET_TICKS);
    },
    (bucket, prev) => {
      if (bucket === -9999 || bucket === prev) {
        return;
      }
      const s = spacing_px;
      const first_raw = bucket * WINDOW_BUCKET_TICKS;
      const from = Math.max(content_first, Math.min(content_last, first_raw));
      const visible = Math.ceil(viewport_width / Math.max(1, s)) + EDGE_BUFFER_TICKS * 2;
      const to = Math.min(content_last, from + Math.max(28, visible));
      runOnJS(apply_window)(from, to);
    },
    [apply_window, content_first, content_last, spacing_px, tick_count, viewport_width],
  );

  const tick_indices = useMemo(() => {
    const from = Math.max(content_first, Math.min(window_range.from, content_last));
    const to = Math.max(from, Math.min(window_range.to, content_last));
    const out: number[] = [];
    for (let i = from; i <= to; i++) {
      out.push(i);
    }
    return out;
  }, [content_first, content_last, window_range.from, window_range.to]);

  const r_strip_style = useAnimatedStyle(() => {
    return {
      transform: [{ translateX: scroll_offset.value }],
    };
  }, []);

  return (
    <Animated.View
      style={[
        styles.strip,
        {
          width: strip_width,
          height: max_line_height,
        },
        r_strip_style,
      ]}>
      {tick_indices.map(tick_index => {
        const is_ghost = tick_index < 0 || tick_index > last_valid;
        const is_big = positive_mod(line_value_base + tick_index, major_every) === 0;
        const tick_height = is_big ? max_line_height : min_line_height;
        const offset_y = (max_line_height - tick_height) / 2;
        const tick_width = is_big ? line_width * 1.45 : line_width;
        /** Center each tick on `tick_index * spacing` (matches snap targets exactly). */
        const left = tick_index * spacing_px - tick_width / 2;
        const base_opacity = is_ghost ? (is_big ? 0.28 : 0.16) : is_big ? 1 : 0.88;

        return (
          <View
            key={tick_index}
            style={{
              position: 'absolute',
              left,
              top: offset_y,
              width: tick_width,
              height: tick_height,
              borderRadius: tick_width,
              backgroundColor: is_big ? big_line_color : line_color,
              opacity: base_opacity,
            }}
          />
        );
      })}
    </Animated.View>
  );
});

const styles = StyleSheet.create({
  strip: {
    position: 'relative',
    overflow: 'visible',
  },
});
